import { isMongoId } from "./seoSlug";

/** Home footer share — copy this deep-link instead of the current page URL. */
export const HOME_SHARE_URL = "https://gitaseva.app/mobileweb";

const MONGO_ID_RE = /[0-9a-fA-F]{24}/g;

/** Find 24-char hex tokens without matchAll (avoids downlevelIteration). */
const findMongoIdTokens = (text: string): string[] => {
  const tokens: string[] = [];
  const re = /[0-9a-fA-F]{24}/g;
  let match: RegExpExecArray | null;
  while ((match = re.exec(text)) !== null) {
    tokens.push(match[0]);
  }
  return tokens;
};

/** True when any path/query/hash segment looks like a Mongo ObjectId. */
const urlExposesMongoId = (url: string): boolean => {
  try {
    const parsed = new URL(url, typeof window !== "undefined" ? window.location.origin : undefined);
    const pathHasId = parsed.pathname
      .split("/")
      .some((seg) => {
        if (!seg) return false;
        try {
          return isMongoId(decodeURIComponent(seg));
        } catch {
          return isMongoId(seg);
        }
      });
    if (pathHasId) return true;

    let queryHasId = false;
    parsed.searchParams.forEach((value) => {
      if (isMongoId(value)) queryHasId = true;
    });
    if (queryHasId) return true;

    const hash = parsed.hash.replace(/^#/, "");
    if (hash) {
      const hashParams = new URLSearchParams(hash.includes("=") ? hash : "");
      let hashParamHasId = false;
      hashParams.forEach((value) => {
        if (isMongoId(value)) hashParamHasId = true;
      });
      if (hashParamHasId) return true;
      // Also catch raw `#<mongoId>` or `#id=<mongoId>` style fragments
      if (isMongoId(hash) || findMongoIdTokens(hash).some((token) => isMongoId(token))) {
        return true;
      }
    }
    return false;
  } catch {
    return findMongoIdTokens(url).length > 0;
  }
};

/**
 * Strip Mongo ObjectIds from path segments, query values, and hash
 * so shared links never expose internal ids.
 */
export const sanitizeShareUrl = (url: string): string => {
  try {
    const parsed = new URL(url, typeof window !== "undefined" ? window.location.origin : undefined);

    const cleanSegments = parsed.pathname
      .split("/")
      .filter((seg) => {
        if (!seg) return true;
        try {
          return !isMongoId(decodeURIComponent(seg));
        } catch {
          return !isMongoId(seg);
        }
      });
    // Drop empty middle segments created by removing an id
    parsed.pathname =
      "/" +
      cleanSegments
        .filter((seg) => seg !== "")
        .join("/");

    // Remove query params whose values are Mongo ids (e.g. ?id=..., ?bookId=...)
    const keysToDelete: string[] = [];
    parsed.searchParams.forEach((value, key) => {
      if (isMongoId(value)) keysToDelete.push(key);
    });
    keysToDelete.forEach((key) => parsed.searchParams.delete(key));

    // Clear hash if it embeds a Mongo id
    if (parsed.hash) {
      const hashBody = parsed.hash.replace(/^#/, "");
      if (
        isMongoId(hashBody) ||
        findMongoIdTokens(hashBody).some((token) => isMongoId(token))
      ) {
        parsed.hash = "";
      }
    }

    return parsed.toString();
  } catch {
    // Fallback: scrub bare 24-char hex tokens from the string
    return url.replace(MONGO_ID_RE, "");
  }
};

/** Log-safe URL — Mongo ids replaced so console never prints them. */
const redactUrlForLog = (url: string): string =>
  url.replace(MONGO_ID_RE, "[id-redacted]");

/**
 * Resolve the safest public URL to share:
 * 1) explicit override (e.g. home deep-link)
 * 2) <link rel="canonical"> when present and clean
 * 3) current location, sanitized
 */
const resolveShareUrl = (override?: string): string => {
  if (override) {
    return sanitizeShareUrl(override);
  }

  if (typeof document !== "undefined") {
    const canonical = document
      .querySelector('link[rel="canonical"]')
      ?.getAttribute("href");
    if (canonical) {
      const fromCanonical = sanitizeShareUrl(canonical);
      if (!urlExposesMongoId(fromCanonical)) {
        return fromCanonical;
      }
    }
  }

  const fromLocation =
    typeof window !== "undefined" ? window.location.href : "";
  return sanitizeShareUrl(fromLocation);
};

/**
 * Copy a share URL to the clipboard.
 * Defaults to canonical / current page URL; never copies a Mongo ObjectId.
 */
export async function copyShareUrl(url?: string): Promise<boolean> {
  const logdetails: string[] = [];
  logdetails.push("action=copyShareUrl");

  const target = resolveShareUrl(url);
  logdetails.push(`url=${redactUrlForLog(target)}`);

  if (!target) {
    logdetails.push("result=empty_url");
    console.log("copyShareUrl ->", logdetails.join(" | "));
    return false;
  }

  if (urlExposesMongoId(target)) {
    // Last-resort guard — do not put an id-bearing URL on the clipboard
    logdetails.push("result=blocked_id_in_url");
    console.log("copyShareUrl ->", logdetails.join(" | "));
    return false;
  }

  try {
    await navigator.clipboard.writeText(target);
    logdetails.push("result=success");
    console.log("copyShareUrl ->", logdetails.join(" | "));
    return true;
  } catch (err: any) {
    logdetails.push("result=error");
    logdetails.push(`message=${err?.message || "unknown"}`);
    console.log("copyShareUrl ->", logdetails.join(" | "));
    return false;
  }
}
